import { Button, Space, TableProps } from "antd";
import { useEffect } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import BackButtonIcon from "../../../../assets/icons/BackButtonIcon";
import EditIcon from "../../../../assets/icons/EditIcon";
import UserIcon from "../../../../assets/icons/UserIcon";
import ViewIcon from "../../../../assets/icons/ViewIcon";
import EmployeeInfoItem from "../../../../components/common/EmployeeInfoItem";
import InfoTitle from "../../../../components/common/InfoTitle";
import TopCard from "../../../../components/common/TopCard";
import LeaveProrateUpdate from "../../../../components/Settings/Leave/LeaveProrateUpdate";
import ConfirmModal from "../../../../components/ui/ConfirmModal";
import CustomTable from "../../../../components/ui/CustomTable";
import Loader from "../../../../components/ui/Loader";
import {
  useGetEmployeeProrateListQuery,
  useSingleEmployeeProrateRunMutation,
} from "../../../../features/leaveProrate/leaveProrateApi";
import useError from "../../../../hooks/useError";
import useModal from "../../../../hooks/useModal";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import { formatDate } from "../../../../lib/time";
import { toast } from "../../../../lib/toast";
import { GetEmployeeProrateListResponseType } from "../../../../types";
import { addKeyInArray, backLink } from "../../../../utils/utils";
import NotFound from "../../../NotFound";
import { leaveSettingsTabList } from "./LeaveType";

export default function LeaveProrateEmployee() {
  const { viewId } = useParams();
  const location = useLocation();
  const { open, handleOpen, handleClose } = useModal<undefined, "AUTO">();
  const { isLoading, isError, error, data } = useGetEmployeeProrateListQuery(
    {
      params: { business_member_id: Number(viewId) },
    },
    {
      skip: !viewId,
    }
  );
  const [
    singleEmployeeProrateRun,
    {
      isLoading: isRunLoading,
      isError: isRunError,
      error: runError,
      isSuccess: isRunSuccess,
    },
  ] = useSingleEmployeeProrateRunMutation();
  useError(isError, error, "Get employee leave data fail");
  useError(isRunError, runError, "Auto prorate run fail");

  const prorateEmployee = data?.data?.prorate_list || [];

  const columns: TableProps<
    GetEmployeeProrateListResponseType["data"]["prorate_list"][0]
  >["columns"] = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => index + 1,
    },
    {
      title: "Applied Date",
      dataIndex: "created_at",
      render: (val) => (val ? formatDate(val) : "n/a"),
    },

    {
      title: "Leave Type",
      dataIndex: "leave_type",
      render: (val) => val?.title || "n/a",
    },
    {
      title: "Used Days",
      dataIndex: "used_days",
      align: "center",
      render: (val) => (val || 0) + " Days",
    },
    {
      title: "Remaining Days",
      dataIndex: "remaining_days",
      align: "center",
      render: (val) => (val || 0) + " Days",
    },
    {
      title: "Total Days",
      dataIndex: "total_days",
      align: "center",
      render: (val) => (val || 0) + " Days",
    },
    {
      title: "Note",
      dataIndex: "note",
      align: "center",
      render: (val) => val ?? "n/a",
    },
    {
      title: "Type",
      dataIndex: "is_auto_prorated",
      align: "center",
      render: (val) => (val ? "Auto" : "Manual"),
    },
    {
      title: "Action",
      render: (val) => (
        <Space>
          <Link to={`${backLink(location.pathname, 2)}/${viewId}/${val?.id}`}>
            <Button
              className="action-button"
              size="small"
              icon={<ViewIcon />}
            />
          </Link>
          <Button
            className="action-button"
            size="small"
            icon={<EditIcon />}
            onClick={() =>
              handleOpen({
                type: "UPDATE",
                data: val,
              })
            }
          />
        </Space>
      ),
    },
  ];

  useEffect(() => {
    if (isRunSuccess) {
      toast("success", "Auto prorate run successfully");

      return handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRunSuccess]);

  if (error && "status" in error && error.status === 404) {
    return <NotFound title="Sorry, data not found" />;
  }

  return (
    <SidebarLayout title="Leave Settings" tabList={leaveSettingsTabList}>
      {open.type === "UPDATE" && (
        <LeaveProrateUpdate
          open={open.open}
          type={open.type}
          data={open.data}
          handleClose={handleClose}
        />
      )}

      {open?.type === "AUTO" && (
        <ConfirmModal
          open={open?.open}
          handleOk={() => {
            if (viewId)
              singleEmployeeProrateRun({
                id: Number(viewId),
                body: {
                  is_prorated: "yes",
                  prorate_type: "profile_complete",
                },
              });
          }}
          handleClose={handleClose}
          okText="Yes"
          confirmTitle="Auto prorate"
          questionText="Do you want to run auto prorate for this user?"
          okLoading={isRunLoading}
        />
      )}

      <TopCard
        leftChildren={
          <Link to={backLink(location.pathname, 2)}>
            <Button className="normal-dark-button" icon={<BackButtonIcon />}>
              Back
            </Button>
          </Link>
        }
      >
        <Button
          className="outline-button-primary"
          size="middle"
          onClick={() => handleOpen({ type: "AUTO" })}
          loading={isRunLoading}
        >
          Run User Auto Prorate
        </Button>
      </TopCard>

      <div className="grid md:grid-cols-2 gap-6 mb-6">
        {isLoading ? (
          <div className="md:col-span-2">
            <Loader />
          </div>
        ) : (
          <div className="p-6 bg-white rounded-md border border-extra-light">
            <Space
              className="w-full mb-6 justify-between"
              align="center"
              size={8}
            >
              <InfoTitle
                buttonClassName="disabled:!text-neutral"
                icon={<UserIcon />}
              >
                Employee Info
              </InfoTitle>
            </Space>

            <EmployeeInfoItem
              img={prorateEmployee?.[0]?.profile?.pro_pic}
              name={prorateEmployee?.[0]?.profile?.name}
              id={prorateEmployee?.[0]?.employee_id}
              designation={prorateEmployee?.[0]?.profile?.designation_name}
              department={prorateEmployee?.[0]?.profile?.department_name}
              email={prorateEmployee?.[0]?.profile?.email}
            />
          </div>
        )}
      </div>

      <div>
        <CustomTable
          loading={isLoading}
          columns={columns}
          dataSource={addKeyInArray(prorateEmployee)}
          className="header-table-color"
        />
      </div>
    </SidebarLayout>
  );
}
